import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { McqQuestion } from '../../fetchSurvey/types/mcq';
import type { RankOrderQuestion } from '../../fetchSurvey/types/rankOrder';
import type { SurveyQuestion } from '../../fetchSurvey/types';
import type { AnswerLogic } from '../../fetchSurvey/types/logic';
import type { ScaleColumn, StatementRow } from '../../fetchSurvey/types/shared';
import type { LogicContext } from '../types';
import { evaluateAnswerLogic } from '../evaluator/evaluateAnswerLogic';
import { getScaleColumns, getStatementRows } from './questionAccess';

export function isAnswerLogicVisible(
  answerLogic: AnswerLogic | undefined,
  context: LogicContext
): boolean {
  return evaluateAnswerLogic(answerLogic, context);
}

export function isStatementRowVisible(row: StatementRow, context: LogicContext): boolean {
  return isAnswerLogicVisible(row.answerLogic, context);
}

export function getVisibleStatementRows(
  question: SurveyQuestion,
  context: LogicContext
): StatementRow[] {
  const rows = getStatementRows(question);
  if (rows.length === 0) {
    return [];
  }

  return rows.filter(row => isStatementRowVisible(row, context));
}

export function getVisibleMcqOptions(question: SurveyQuestion, context: LogicContext) {
  if (question.type !== QUESTION_TYPE.MCQ) {
    return [];
  }

  const mcq = question as McqQuestion;
  return mcq.options.filter(option => isAnswerLogicVisible(option.answerLogic, context));
}

export function getVisibleRankOrderOptions(question: SurveyQuestion, context: LogicContext) {
  if (question.type !== QUESTION_TYPE.RANK_ORDER) {
    return [];
  }

  const rankOrderQuestion = question as RankOrderQuestion;
  return rankOrderQuestion.options.filter(option =>
    isAnswerLogicVisible(option.answerLogic, context)
  );
}

export function getVisibleScaleColumns(
  question: SurveyQuestion,
  context: LogicContext
): ScaleColumn[] {
  const columns = getScaleColumns(question);
  if (columns.length === 0) {
    return [];
  }

  return columns.filter(column => isAnswerLogicVisible(column.answerLogic, context));
}
